import React, {useEffect, useState} from 'react';
import Footer from "./footer";
import Header from "./header";
import ReactGA from "react-ga4";
import Reveal from "./reveal";
import Showcase from "./showcase";
import StoreBadges from "./storeBadges";
import topo from '../assets/hero-topo.svg';
import logo from '../assets/peticle-icon.png';
import {faqs, features, plans, showcase, species, steps, urgencyLevels} from './content';

ReactGA.send(window.location.pathname);

function SectionHeading({eyebrow, title, subtitle, light = false}: {
    eyebrow: string;
    title: React.ReactNode;
    subtitle?: string;
    light?: boolean;
}) {
    return (
        <Reveal className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
            <span className={`inline-block text-xs font-bold uppercase tracking-[0.18em] ${light ? 'text-[#B9DDB0]' : 'text-brand'}`}>
                {eyebrow}
            </span>
            <h2 className={`mt-3 text-3xl md:text-[2.75rem] md:leading-[1.1] font-extrabold tracking-tight ${light ? 'text-white' : 'text-text'}`}>
                {title}
            </h2>
            {subtitle && (
                <p className={`mt-4 text-lg leading-relaxed ${light ? 'text-white/75' : 'text-soft'}`}>{subtitle}</p>
            )}
        </Reveal>
    );
}

function Hero() {
    const [left, center, right] = [showcase[1], showcase[0], showcase[2]];
    return (
        <section className="relative overflow-hidden bg-[#2F5A3C] text-white">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#5E9B6C_0%,#3F7350_35%,#2A4F35_75%)]"/>
            <img src={topo} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-70 hero-drift pointer-events-none"/>
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-[#2A4F35]/60"/>

            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-32 md:pt-40 pb-20 md:pb-28 grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
                <div className="text-center lg:text-left">
                    <div className="hero-in inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur px-3.5 py-1.5 text-sm font-semibold">
                        Pet health companion
                    </div>

                    <h1 className="hero-in [animation-delay:100ms] mt-6 text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-[4.1rem] font-extrabold tracking-tight">
                        Your pet can't tell you when something's off.
                        <span className="block mt-1 bg-gradient-to-r from-[#D6F0C8] to-[#9BD1A4] bg-clip-text text-transparent">
                            Peticle can.
                        </span>
                    </h1>

                    <p className="hero-in [animation-delay:200ms] mt-6 text-lg sm:text-xl text-white/80 leading-relaxed max-w-xl mx-auto lg:mx-0">
                        Snap a photo for instant breed, health and fecal insights — then keep weights,
                        vet visits, vaccines and reminders together in one calm, organised place.
                    </p>

                    <div className="hero-in [animation-delay:300ms] mt-9 flex justify-center lg:justify-start">
                        <StoreBadges light/>
                    </div>

                    <ul className="hero-in [animation-delay:400ms] mt-9 flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-white/75">
                        {['Free to start', 'Results in seconds', 'iOS & Android'].map((t) => (
                            <li key={t} className="flex items-center gap-2">
                                <svg className="h-4 w-4 text-[#B9DDB0]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/>
                                </svg>
                                {t}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative h-[440px] sm:h-[520px] lg:h-[580px]">
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-[#9BD1A4]/30 blur-3xl"/>
                    <div className="hero-phone hero-phone-left absolute left-1/2 top-1/2 w-[42%] max-w-[220px]">
                        <img src={left.image} alt={`${left.tag} screen`} className="w-full rounded-[1.6rem] shadow-2xl shadow-black/40 ring-1 ring-white/10"/>
                    </div>
                    <div className="hero-phone hero-phone-right absolute left-1/2 top-1/2 w-[42%] max-w-[220px]">
                        <img src={right.image} alt={`${right.tag} screen`} className="w-full rounded-[1.6rem] shadow-2xl shadow-black/40 ring-1 ring-white/10"/>
                    </div>
                    <div className="hero-phone hero-phone-center absolute left-1/2 top-1/2 w-[54%] max-w-[270px]">
                        <img src={center.image} alt={`${center.tag} screen`} className="w-full rounded-[1.8rem] shadow-2xl shadow-black/50 ring-1 ring-white/20"/>
                    </div>

                    <div className="float-chip absolute left-0 sm:left-2 top-[60%] hidden sm:flex items-center gap-3 rounded-2xl bg-white text-text px-4 py-3 shadow-xl">
                        <span className="text-2xl">🧬</span>
                        <span className="leading-tight">
                            <span className="block text-xs text-soft">Greyhound match</span>
                            <span className="block text-sm font-bold">88% confidence</span>
                        </span>
                    </div>
                    <div className="float-chip [animation-delay:-3s] absolute right-0 sm:right-2 bottom-[12%] hidden sm:flex items-center gap-3 rounded-2xl bg-white text-text px-4 py-3 shadow-xl">
                        <span className="h-2.5 w-2.5 rounded-full bg-brand"/>
                        <span className="leading-tight">
                            <span className="block text-xs text-soft">Health status</span>
                            <span className="block text-sm font-bold">Normal · scanned today</span>
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
}

function SpeciesStrip() {
    const items = [...species, ...species, ...species, ...species];
    return (
        <section className="border-y border-border bg-surface py-5 overflow-hidden" aria-label="Supported pets">
            <div className="flex items-center gap-6 max-w-6xl mx-auto px-4 sm:px-6">
                <span className="shrink-0 text-xs font-bold uppercase tracking-[0.18em] text-soft">Built for</span>
                <div className="relative flex-1 overflow-hidden marquee-mask">
                    <div className="marquee flex w-max gap-10">
                        {items.map((s, i) => (
                            <span key={i} className="flex items-center gap-2 text-base font-semibold text-text whitespace-nowrap">
                                <span className="text-2xl">{s.icon}</span>{s.label}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function HowItWorks() {
    return (
        <section id="how" className="scroll-mt-20 py-24 md:py-32">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <SectionHeading
                    eyebrow="How it works"
                    title="From photo to peace of mind"
                    subtitle="Three steps, under a minute. No jargon, no guesswork."
                />
                <div className="relative grid md:grid-cols-3 gap-6">
                    <div className="hidden md:block absolute top-11 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-brand/40 to-transparent"/>
                    {steps.map((s, i) => (
                        <Reveal key={s.n} delay={i * 120} className="relative text-center px-4">
                            <div className="mx-auto h-[5.5rem] w-[5.5rem] rounded-full bg-surface ring-1 ring-border shadow-lg shadow-brand/10 flex items-center justify-center">
                                <span className="text-2xl font-extrabold text-brand tabular-nums">{s.n}</span>
                            </div>
                            <h3 className="mt-6 text-xl font-bold text-text">{s.title}</h3>
                            <p className="mt-2 text-soft leading-relaxed">{s.description}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Tour() {
    return (
        <section id="screens" className="scroll-mt-20 py-24 md:py-32 bg-gradient-to-b from-brand-light/60 via-brand-light/30 to-bg">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <SectionHeading
                    eyebrow="Take the tour"
                    title="Everything your pet needs, in your pocket"
                    subtitle="Designed to be calm and clear — so you spend less time worrying and more time with them."
                />
                <Showcase/>
            </div>
        </section>
    );
}

function Features() {
    return (
        <section id="features" className="scroll-mt-20 py-24 md:py-32">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <SectionHeading
                    eyebrow="Features"
                    title="Spot changes early. Keep everything together."
                    subtitle="Smart scans when you need answers, simple logs for everything in between."
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {features.map((f, i) => (
                        <Reveal
                            key={f.title}
                            delay={(i % 4) * 80}
                            className={`group relative overflow-hidden rounded-3xl bg-surface p-7 ring-1 ring-border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10 hover:ring-brand/40 ${
                                f.span === 'wide' ? 'sm:col-span-2' : ''
                            }`}
                        >
                            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-brand-light opacity-0 transition-opacity duration-500 group-hover:opacity-100"/>
                            <div className="relative">
                                <div className="h-12 w-12 rounded-2xl bg-brand-light flex items-center justify-center text-2xl transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6">
                                    {f.icon}
                                </div>
                                <h3 className="mt-5 text-lg font-bold text-text">{f.title}</h3>
                                <p className="mt-2 text-sm text-soft leading-relaxed">{f.description}</p>
                                {f.points && (
                                    <ul className="mt-4 flex flex-wrap gap-2">
                                        {f.points.map((p) => (
                                            <li key={p} className="rounded-full bg-bg ring-1 ring-border px-3 py-1 text-xs font-semibold text-brand-light-text">
                                                {p}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Urgency() {
    return (
        <section className="relative overflow-hidden py-24 md:py-28 bg-[#24432F]">
            <img src={topo} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none"/>
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
                <SectionHeading
                    light
                    eyebrow="Clear next steps"
                    title="Know when it can wait — and when it can't"
                    subtitle="Every fecal scan and symptom assessment ends with an urgency level and practical vet guidance, not a wall of medical terms."
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {urgencyLevels.map((u, i) => (
                        <Reveal key={u.label} delay={i * 100} className="rounded-3xl bg-white/[0.06] ring-1 ring-white/10 backdrop-blur p-6">
                            <div className="flex items-center gap-3">
                                <span className="h-1.5 flex-1 rounded-full bg-white/10 overflow-hidden">
                                    <span className="block h-full rounded-full" style={{width: `${(i + 1) * 25}%`, background: u.bg}}/>
                                </span>
                                <span className="text-xs font-bold tabular-nums text-white/50">{i + 1}/4</span>
                            </div>
                            <span className="mt-5 inline-flex rounded-full px-3 py-1 text-sm font-bold" style={{background: u.bg, color: u.color}}>
                                {u.label}
                            </span>
                            <p className="mt-3 text-white/75 leading-relaxed">{u.desc}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Pricing() {
    const [annual, setAnnual] = useState(false);
    return (
        <section id="pricing" className="scroll-mt-20 py-24 md:py-32">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <SectionHeading
                    eyebrow="Pricing"
                    title="A plan for every pet lover"
                    subtitle="Start free. Upgrade when you need more scans or more pets — unused analyses roll over."
                />

                <Reveal className="flex justify-center mb-10">
                    <div className="relative inline-flex rounded-full bg-surface ring-1 ring-border p-1 shadow-sm">
                        <span
                            className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-brand transition-transform duration-300 ${annual ? 'translate-x-full' : ''}`}
                        />
                        {[false, true].map((isAnnual) => (
                            <button
                                key={String(isAnnual)}
                                type="button"
                                onClick={() => setAnnual(isAnnual)}
                                className={`relative z-10 w-32 rounded-full py-2 text-sm font-semibold transition-colors ${
                                    annual === isAnnual ? 'text-white' : 'text-soft hover:text-text'
                                }`}
                            >
                                {isAnnual ? 'Annual' : 'Monthly'}
                                {isAnnual && (
                                    <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${annual ? 'bg-white/20' : 'bg-brand-light text-brand-light-text'}`}>
                                        −17%
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
                    {plans.map((p, i) => {
                        const price = annual ? p.annual : p.monthly;
                        return (
                            <Reveal
                                key={p.id}
                                delay={i * 90}
                                className={`relative flex flex-col rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 ${
                                    p.highlight
                                        ? 'bg-text text-white shadow-2xl shadow-[#2D6A8F]/25 lg:-my-3 lg:py-10'
                                        : 'bg-surface ring-1 ring-border hover:shadow-xl hover:shadow-brand/10'
                                }`}
                            >
                                {p.highlight && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-[#2D6A8F] to-[#4A7C59] px-3 py-1 text-xs font-bold text-white shadow-md whitespace-nowrap">
                                        Most popular
                                    </span>
                                )}
                                <div className="flex items-center gap-2">
                                    <span className="text-2xl">{p.emoji}</span>
                                    <h3 className="text-xl font-bold" style={{color: p.highlight ? '#fff' : p.color}}>{p.name}</h3>
                                </div>
                                <p className={`mt-1 text-sm ${p.highlight ? 'text-white/60' : 'text-soft'}`}>{p.tagline}</p>

                                <div className="mt-6 flex items-baseline gap-1">
                                    <span className="text-4xl font-extrabold tracking-tight tabular-nums">
                                        {price === 0 ? '$0' : `$${price.toFixed(2)}`}
                                    </span>
                                    <span className={`text-sm ${p.highlight ? 'text-white/60' : 'text-soft'}`}>
                                        {price === 0 ? 'forever' : annual ? '/ year' : '/ month'}
                                    </span>
                                </div>
                                <p className={`mt-1 h-5 text-xs ${p.highlight ? 'text-white/50' : 'text-soft'}`}>
                                    {annual && price > 0 ? `≈ $${(p.annual / 12).toFixed(2)} / month` : ''}
                                </p>

                                <ul className="mt-6 flex flex-col gap-3 text-sm flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2.5">
                                            <svg className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill={p.highlight ? '#9BD1A4' : p.color} aria-hidden="true">
                                                <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 111.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd"/>
                                            </svg>
                                            <span className={p.highlight ? 'text-white/85' : 'text-text'}>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Reveal>
                        );
                    })}
                </div>
                <p className="mt-8 text-center text-xs text-soft">
                    Prices in USD. Subscriptions are billed through the App Store or Google Play and local pricing may vary.
                </p>
            </div>
        </section>
    );
}

function Faq() {
    const [open, setOpen] = useState<number | null>(0);
    return (
        <section id="faq" className="scroll-mt-20 py-24 md:py-32 bg-surface border-y border-border">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
                <SectionHeading eyebrow="FAQ" title="Questions, answered"/>
                <div className="flex flex-col gap-3">
                    {faqs.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <Reveal key={f.q} delay={i * 60} className={`rounded-2xl ring-1 transition-colors ${isOpen ? 'ring-brand/40 bg-bg' : 'ring-border'}`}>
                                <button
                                    type="button"
                                    aria-expanded={isOpen}
                                    onClick={() => setOpen(isOpen ? null : i)}
                                    className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                                >
                                    <span className="font-bold text-text">{f.q}</span>
                                    <span className={`shrink-0 h-8 w-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-brand text-white rotate-45' : 'bg-brand-light text-brand'}`}>
                                        <svg width="14" height="14" viewBox="0 0 14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                            <path d="M7 1v12M1 7h12"/>
                                        </svg>
                                    </span>
                                </button>
                                <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                                    <p className="overflow-hidden px-6 text-soft leading-relaxed">
                                        <span className="block pb-5">{f.a}</span>
                                    </p>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function FinalCta() {
    return (
        <section className="py-24 md:py-32">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <Reveal className="relative overflow-hidden rounded-[2.5rem] bg-[#2F5A3C] px-6 py-16 md:px-16 md:py-20 text-center">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,#5E9B6C_0%,transparent_60%)]"/>
                    <img src={topo} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-50 hero-drift pointer-events-none"/>
                    <div className="relative">
                        <img src={logo} alt="" width={64} height={64} className="wiggle inline-block rounded-2xl shadow-xl shadow-black/30 ring-4 ring-white/15"/>
                        <h2 className="mt-4 text-3xl md:text-5xl font-extrabold tracking-tight text-white max-w-2xl mx-auto">
                            Give them the care they can't ask for.
                        </h2>
                        <p className="mt-4 text-lg text-white/75 max-w-xl mx-auto">
                            Download Peticle free and run your first scans today.
                        </p>
                        <div className="mt-9 flex justify-center">
                            <StoreBadges light/>
                        </div>
                    </div>
                </Reveal>

                <Reveal className="mt-10 max-w-3xl mx-auto flex gap-4 items-start rounded-2xl bg-warn-light/60 ring-1 ring-[#FCD34D] p-5">
                    <span className="text-xl" aria-hidden="true">⚠️</span>
                    <p className="text-sm text-[#78350F] leading-relaxed">
                        <strong>Important:</strong> Peticle provides informational insights only. It is not a veterinary
                        diagnostic tool and does not replace professional veterinary advice, examination, or treatment.
                        Always consult a qualified vet for your pet's health concerns.
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

function Home() {
    // Anchor links from other pages (e.g. /#pricing) land before React renders the target.
    useEffect(() => {
        const hash = window.location.hash;
        if (hash) document.querySelector(hash)?.scrollIntoView();
    }, []);

    return (
        <div className="min-h-screen flex flex-col bg-bg font-sans text-text">
            <Header overlay/>
            <main className="flex-1">
                <Hero/>
                <SpeciesStrip/>
                <HowItWorks/>
                <Tour/>
                <Features/>
                <Urgency/>
                <Pricing/>
                <Faq/>
                <FinalCta/>
            </main>
            <Footer/>
        </div>
    );
}

export default Home;
